<template>
<div>
   <!-- Fullscreen Chart Dialog -->
  <v-dialog v-model="chartFullscreen" fullscreen hide-overlay transition="dialog-bottom-transition">
    <v-card :dark="isDark">
      <v-card-title class="primary white--text d-flex align-center">
        <span>Chart - Full Screen</span>
        <v-spacer />
        <v-btn icon dark @click="chartFullscreen = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="pa-4" style="height: calc(100vh - 64px);">
        <div id="chart-fullscreen" style="width:100%;height:100%;"></div>
      </v-card-text>
    </v-card>
  </v-dialog>
  <div id="chart" :class="agGridTheme" v-show="showChart"></div>
  <ag-grid-vue
    :sideBar="sideBar"
    :rowData="rowData"
    multiSortKey="ctrl"
    :animateRows="true"
    :enableCharts="true"
    pivotPanelShow="always"
    :columnDefs="columnDefs"
    rowGroupPanelShow="always"
    :gridOptions="gridOptions"
    :enableRangeSelection="true"
    :class="`${agGridTheme} mt-2`"
    :localeText="agGridLocaleText"
    :defaultColDef="defaultColDef"
    :style="`width: 100%; height: ${showChart ? '450' : '600'}px;`"
    @sort-changed="onStateChange"
    @filter-changed="onStateChange"
    @column-pinned="onStateChange"
    @column-visible="onStateChange"
    @column-resized="onStateChange"
    @column-moved="onStateChange"
    @column-pivot-mode-changed="onPivotModeChange"
    @column-pivot-changed="onStateChange"
    @column-row-group-changed="onStateChange"
    @column-value-changed="onStateChange"
    @first-data-rendered="visualizeData"
    :customChartThemes="customChartThemes"
    :chartThemes="chartThemes"
  ></ag-grid-vue>
</div>
</template>

<script>
// eslint-disable-next-line
import 'ag-grid-community/dist/styles/ag-grid.css';
// eslint-disable-next-line
import 'ag-grid-community/dist/styles/ag-theme-balham.css';
// eslint-disable-next-line
import 'ag-grid-community/dist/styles/ag-theme-balham-dark.css';
// eslint-disable-next-line
import { AgGridVue } from 'ag-grid-vue';
import {
  mapState,
  mapMutations,
  mapGetters,
  mapActions,
} from 'vuex';

export default {
  name: 'ReportGrid',
  components: {
    AgGridVue,
  },
  data() {
    return {
      rowData: [],
      aggFunc: null,
      gridApi: null,
      columnDefs: [],
      gridOptions: null,
      gridColumnApi: null,
      defaultColDef: null,
      rowGroupPanelShow: null,
      chartThemes: null,
      customChartThemes: null,
      timeout: null,
      onThemeChangeColor: '#080707',
      chartFullscreen: false,
      sideBar: {
        toolPanels: [
          {
            id: 'columns',
            labelDefault: 'Columns',
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
              suppressRowGroups: false,
              suppressValues: false,
              suppressPivots: true,
              suppressPivotMode: false,
              suppressColumnFilter: false,
              suppressColumnSelectAll: false,
              suppressColumnExpandAll: false,
            },
          },
          {
            id: 'filters',
            labelDefault: 'Filters',
            labelKey: 'filters',
            iconKey: 'filter',
            toolPanel: 'agFiltersToolPanel',
          },
        ],
        defaultToolPanel: '',
      },
    };
  },
  created() {
    this.gridOptions = {
      groupDefaultExpanded: -1,
      getRowStyle: (params) => {
        // Bold only rows that have group children (not leaf-only)
        if (params.node && params.node.group) {
          const hasGroupChildren = params.node.childrenAfterGroup
            && params.node.childrenAfterGroup.some(
              (child) => child.group,
            );
          // If it has group children OR is a top-level group, bold it
          if (hasGroupChildren || params.node.level === 0) {
            return { fontWeight: '700' };
          }
        }
        return { fontWeight: '400' };
      },
    };
    this.defaultColDef = {
      filter: true,
      sortable: true,
      resizable: true,
      enableValue: true,
      enablePivot: true,
      enableRowGroup: true,
      floatingFilter: true,
    };
  },
  beforeMount() {
    if (this.isDark) {
      this.onThemeChangeColor = '#fff';
    } else {
      this.onThemeChangeColor = '#080707';
    }
    this.initChartThemes();
  },
  mounted() {
    this.gridApi = this.gridOptions.api;
    this.gridColumnApi = this.gridOptions.columnApi;
    // Inject resizers after ag-Grid renders tool panel
    this.$nextTick(() => {
      setTimeout(() => this.injectPanelResizers(), 500);
    });
  },
  beforeDestroy() {
    clearTimeout(this.timeout);
  },
  computed: {
    ...mapState('helper', ['isDark']),
    ...mapGetters('helper', ['agGridLocaleText', 'agGridTheme']),
    ...mapState('reports', ['report', 'reportMapping', 'showChart']),
    ...mapGetters('reports', ['isBaseReport', 'gridObject', 'reportTitle']),
    aggType() {
      return this.reportMapping ? this.$i18n.t(`${this.reportMapping.aggregationType}`) : '';
    },
  },
  watch: {
    report(val) {
      if (val) {
        this.aggFunc = val.aggFunc || null;
      }
      if (val && val.cols) {
        this.columnDefs = val.cols.map((col) => ({
          headerName: this.getHeaderName(col),
          field: col.name,
          colId: col.name,
          filter: this.getColumnFilter(col),
        }));
      }
      if (val && val.reportData) {
        this.rowData = val.reportData;
        this.visualizeData();
        this.timeout = setTimeout(() => {
          this.visualizeData();
        }, 200);
      }
    },
    isDark: {
      handler(val) {
        if (val) {
          this.onThemeChangeColor = '#fff';
          this.initChartThemes();
          this.executeReport();
        } else {
          this.onThemeChangeColor = '#080707';
          this.initChartThemes();
          this.executeReport();
        }
      },
    },
  },
  methods: {
    ...mapMutations('reports', ['setGridState', 'setIsPDFVisible']),
    ...mapActions('reports', ['executeReport']),
    injectPanelResizers() {
      const panel = this.$el.querySelector('.ag-column-panel');
      if (!panel) return;
      const sections = panel.children;
      if (!sections || sections.length < 2) return;
      // Make panel a flex column
      panel.style.display = 'flex';
      panel.style.flexDirection = 'column';
      panel.style.height = '100%';
      panel.style.overflow = 'hidden';
      // Set each section to be scrollable with min heights
      for (let i = 0; i < sections.length; i += 1) {
        const section = sections[i];
        section.style.overflow = 'auto';
        section.style.minHeight = '50px';
        if (i === 0) {
          // Columns list - takes flex space
          section.style.flex = '1 1 auto';
        } else {
          // Row Groups, Values, Pivot - fixed initial height
          section.style.flex = '0 0 120px';
        }
      }
      // Insert drag handles between sections
      const sectionArray = Array.from(sections);
      for (let i = sectionArray.length - 1; i > 0; i -= 1) {
        const handle = document.createElement('div');
        handle.className = 'panel-section-resizer';
        handle.style.cssText = [
          'height: 5px',
          'min-height: 5px',
          'cursor: row-resize',
          'background: transparent',
          'border-top: 1px solid #dde2eb',
          'border-bottom: 1px solid #dde2eb',
          'transition: background 0.2s',
          'flex-shrink: 0',
        ].join(';');
        handle.addEventListener('mouseenter', () => {
          handle.style.background = 'rgba(76,175,80,0.3)';
        });
        handle.addEventListener('mouseleave', () => {
          if (!handle.dataset.active) {
            handle.style.background = 'transparent';
          }
        });
        this.attachResizeLogic(handle, sectionArray[i - 1], sectionArray[i]);
        panel.insertBefore(handle, sectionArray[i]);
      }
    },
    attachResizeLogic(handle, aboveEl, belowEl) {
      let startY = 0;
      let aboveH = 0;
      let belowH = 0;
      const onMouseMove = (e) => {
        const diff = e.clientY - startY;
        const newAbove = Math.max(50, aboveH + diff);
        const newBelow = Math.max(50, belowH - diff);
        aboveEl.style.flex = `0 0 ${newAbove}px`;
        belowEl.style.flex = `0 0 ${newBelow}px`;
      };
      const onMouseUp = () => {
        handle.dataset.active = '';
        handle.style.background = 'transparent';
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };
      handle.addEventListener('mousedown', (e) => {
        e.preventDefault();
        startY = e.clientY;
        aboveH = aboveEl.getBoundingClientRect().height;
        belowH = belowEl.getBoundingClientRect().height;
        handle.dataset.active = 'true';
        handle.style.background = 'rgba(76,175,80,0.5)';
        document.body.style.cursor = 'row-resize';
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
      });
    },
    getHeaderName(col) {
      switch (this.$i18n.locale) {
        case 'zhHans':
          return col.description_cn || col.description;
        case 'hi':
          return col.description_hi || col.description;
        case 'th':
          return col.description_th || col.description;
        case 'de':
          return col.description_de || col.description;
        default:
          return col.description;
      }
    },
    visualizeData() {
      const chartContainer = document.getElementById('chart');
      chartContainer.innerHTML = '';
      chartContainer.style.height = 0;
      if (this.gridColumnApi.isPivotMode()) {
        this.createPivotChart(chartContainer);
      } else {
        this.createRangeChart(chartContainer);
      }
      chartContainer.style.height = '350px';
    },
    createRangeChart(chartContainer) {
      const param = {
        chartType: 'groupedColumn',
        cellRange: {
          columns: this.columnDefs.map((c) => c.field),
        },
        aggFunc: this.aggFunc,
        chartThemeOverrides: {
          common: {
            background: {
              visible: false,
            },
            title: {
              enabled: false,
            },
            legend: { enabled: true },
            navigator: {
              enabled: true,
              height: 15,
            },
          },
        },
        chartContainer,
      };
      this.gridApi.createRangeChart(param);
    },
    createPivotChart(chartContainer) {
      const param = {
        chartType: 'groupedColumn',
        chartThemeOverrides: {
          common: {
            background: {
              visible: false,
            },
            title: {
              enabled: false,
            },
            legend: { enabled: true },
            navigator: {
              enabled: true,
              height: 15,
            },
          },
        },
        chartContainer,
      };
      this.gridApi.createPivotChart(param);
    },
    getColumnFilter(col) {
      const type = col && col.type.toLowerCase();
      switch (type) {
        case 'long':
          return 'agNumberColumnFilter';
        case 'double':
          return 'agNumberColumnFilter';
        case 'number':
          return 'agNumberColumnFilter';
        case 'integer':
          return 'agNumberColumnFilter';
        default:
          return 'agSetColumnFilter';
      }
    },
    togglePDF() {
      const pivotColumns = this.gridColumnApi.getPivotColumns();
      if (pivotColumns.length) {
        this.setIsPDFVisible(false);
      } else {
        this.setIsPDFVisible(true);
      }
    },
    onStateChange() {
      this.togglePDF();
      const filterState = this.gridApi.getFilterModel();
      if (Object.keys(filterState).length === 0) {
        this.executeReport();
      } else {
        this.visualizeData();
      }
      const colState = this.gridColumnApi.getColumnState();
      const groupState = this.gridColumnApi.getColumnGroupState();
      const isPivotMode = this.gridColumnApi.isPivotMode();
      const state = {
        colState,
        groupState,
        filterState,
        isPivotMode,
      };
      this.setGridState(JSON.stringify(state));
    },
    onPivotModeChange() {
      this.visualizeData();
    },
    restoreState() {
      if (!this.isBaseReport) {
        const state = JSON.parse(this.gridObject);
        this.setState(state);
        this.togglePDF();
      } else {
        this.resetState();
      }
    },
    setState(state) {
      this.gridColumnApi.setColumnState(state.colState);
      this.gridColumnApi.setColumnGroupState(state.groupState);
      this.gridApi.setFilterModel(state.filterState);
      this.gridColumnApi.setPivotMode(state.isPivotMode);
    },
    resetState() {
      this.gridColumnApi.resetColumnState();
      this.gridColumnApi.resetColumnGroupState();
      this.gridApi.setFilterModel(null);
      this.gridColumnApi.setPivotMode(false);
    },
    exportGridCSV() {
      const params = {
        fileName: `${this.reportTitle}_${this.aggType}-${new Date().toLocaleString()}`,
      };
      this.gridApi.exportDataAsCsv(params);
    },
    exportGridExcel() {
      const params = {
        fileName: `${this.reportTitle}_${this.aggType}-${new Date().toLocaleString()}`,
      };
      this.gridApi.exportDataAsExcel(params);
    },
    openFullscreenChart() {
      this.chartFullscreen = true;
      this.$nextTick(() => {
        const container = document.getElementById('chart-fullscreen');
        if (container) {
          container.innerHTML = '';
          const themeOverrides = {
            common: {
              background: { visible: false },
              title: { enabled: false },
              legend: { enabled: true },
            },
          };
          if (this.gridColumnApi.isPivotMode()) {
            this.gridApi.createPivotChart({
              chartType: 'groupedColumn',
              chartContainer: container,
              chartThemeOverrides: themeOverrides,
            });
          } else {
            this.gridApi.createRangeChart({
              chartType: 'groupedColumn',
              cellRange: { columns: this.columnDefs.map((c) => c.field) },
              aggFunc: this.aggFunc,
              chartContainer: container,
              chartThemeOverrides: themeOverrides,
            });
          }
        }
      });
    },
    initChartThemes() {
      this.customChartThemes = {
        shopworxTheme: {
          baseTheme: 'ag-pastel',
          palette: {
            fills: [
              '#354493',
              '#21C77C',
              '#2A2F36',
              '#01C1E2',
              '#0172CA',
              '#5C68A8',
              '#4CD195',
              '#3E4249',
            ],
            strokes: ['black'],
          },
          overrides: {
            common: {
              legend: {
                item: {
                  label: {
                    color: this.onThemeChangeColor,
                  },
                },
              },
              axes: {
                category: {
                  label: { color: this.onThemeChangeColor },
                },
                number: {
                  label: { color: this.onThemeChangeColor },
                },
              },
            },
          },
        },
      };
      this.chartThemes = [
        'shopworxTheme',
        'ag-default',
        'ag-material',
        'ag-pastel',
        'ag-vivid',
        'ag-solar',
      ];
    },
  },
};
</script>

<style>
/* ag-Grid Column Tool Panel - resizer handle styling */
.panel-section-resizer:hover {
  background: rgba(76, 175, 80, 0.3) !important;
}

/* Force light mode filter panel styling when using ag-theme-balham */
.ag-theme-balham .ag-tool-panel-wrapper,
.ag-theme-balham .ag-filter-toolpanel,
.ag-theme-balham .ag-side-bar,
.ag-theme-balham .ag-column-panel {
  background-color: #fff !important;
  color: #000 !important;
}

.ag-theme-balham .ag-filter-toolpanel-header,
.ag-theme-balham .ag-filter-toolpanel-group-level-0,
.ag-theme-balham .ag-column-select-header,
.ag-theme-balham .ag-column-select-list {
  background-color: #fff !important;
  color: #000 !important;
}

.ag-theme-balham .ag-header-row-column-filter,
.ag-theme-balham .ag-floating-filter,
.ag-theme-balham .ag-floating-filter-input,
.ag-theme-balham .ag-floating-filter-body {
  background-color: #fff !important;
  color: #000 !important;
}

.ag-theme-balham .ag-popup,
.ag-theme-balham .ag-filter,
.ag-theme-balham .ag-menu {
  background-color: #fff !important;
  color: #000 !important;
}

/* Prevent Vuetify dark theme from affecting ag-Grid in light mode */
.theme--dark .ag-theme-balham .ag-header,
.theme--dark .ag-theme-balham .ag-header-row,
.theme--dark .ag-theme-balham .ag-header-cell,
.theme--dark .ag-theme-balham .ag-floating-filter,
.theme--dark .ag-theme-balham .ag-tool-panel-wrapper,
.theme--dark .ag-theme-balham .ag-side-bar,
.theme--dark .ag-theme-balham .ag-filter {
  background-color: inherit !important;
  color: inherit !important;
}

/* Row group expand/collapse arrows - bold, thicker, black */
.ag-theme-balham .ag-group-expanded .ag-icon,
.ag-theme-balham .ag-group-contracted .ag-icon,
.ag-theme-balham-dark .ag-group-expanded .ag-icon,
.ag-theme-balham-dark .ag-group-contracted .ag-icon {
  color: #000000 !important;
  font-weight: bold !important;
  font-size: 18px !important;
  -webkit-text-stroke: 0.5px #000000;
  text-stroke: 0.5px #000000;
}

.ag-theme-balham-dark .ag-group-expanded .ag-icon,
.ag-theme-balham-dark .ag-group-contracted .ag-icon {
  color: #ffffff !important;
  -webkit-text-stroke: 0.5px #ffffff;
  text-stroke: 0.5px #ffffff;
}

/* Only rows with expand/collapse arrows at top group levels bold */
/* Level 0 and 1 group rows = bold (parent groups) */
.ag-theme-balham .ag-row-level-0 .ag-group-value,
.ag-theme-balham .ag-row-level-1 .ag-group-value,
.ag-theme-balham-dark .ag-row-level-0 .ag-group-value,
.ag-theme-balham-dark .ag-row-level-1 .ag-group-value {
  font-weight: 700 !important;
}

/* Deepest group level and leaf rows = normal weight */
.ag-theme-balham .ag-row-level-2 .ag-group-value,
.ag-theme-balham .ag-row-level-3 .ag-group-value,
.ag-theme-balham .ag-row-level-4 .ag-group-value,
.ag-theme-balham-dark .ag-row-level-2 .ag-group-value,
.ag-theme-balham-dark .ag-row-level-3 .ag-group-value,
.ag-theme-balham-dark .ag-row-level-4 .ag-group-value {
  font-weight: 400 !important;
}
</style>
